// Wrap annotation matches inside a plain-text line with spans that DiffViewer
// can attach hover/click handlers to. Returns an array of React-friendly
// segments: strings (no match) or { match: true, idx, ... } objects.

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function findMatches(text, target, caseSensitive) {
  if (!text || !target) return [];
  const flags = caseSensitive ? 'g' : 'gi';
  const re = new RegExp(escapeRegex(target), flags);
  const matches = [];
  let m;
  while ((m = re.exec(text)) !== null) {
    matches.push({ start: m.index, end: m.index + m[0].length });
    // Avoid zero-width loop
    if (m.index === re.lastIndex) re.lastIndex++;
  }
  return matches;
}

// Find all annotations whose text appears on this line (target side) or whose
// source_text appears (source side), and return them as non-overlapping
// segments to render. `side` is 'source' or 'target'.
//
// Annotations carry the global index they were given on the message - we use
// that index to pair source-side and target-side highlights so a click on one
// can locate the matching span on the other panel.
// `marks` are the copy's handwritten values on this line ({ text, index }): they are matched exactly and win
// over an annotation they overlap, so each shows where on the scan to check it.
export function buildAnnotatedSegments(line, annotations, side, marks = []) {
  if (!line) return [{ text: '' }];
  if ((!annotations || annotations.length === 0) && marks.length === 0) return [{ text: line }];

  const ranges = [];
  // The n-th value with one text stands at its n-th occurrence on the line.
  const taken = {};
  marks.forEach((mark) => {
    const nth = taken[mark.text] || 0;
    taken[mark.text] = nth + 1;
    const occurrence = findMatches(line, mark.text, true)[nth];
    if (occurrence) ranges.push({ ...occurrence, mark, priority: 0 });
  });
  (annotations || []).forEach((ann, idx) => {
    const term = side === 'source' ? ann.source_text : ann.text;
    if (!term) return;
    // Proper nouns are case-sensitive; everything else case-insensitive (matches
    // the rule already used by annotation_service.py).
    const caseSensitive = ann.type === 'proper_noun';
    const matches = findMatches(line, term, caseSensitive);
    matches.forEach(({ start, end }) => {
      ranges.push({ start, end, idx, ann, priority: 1 });
    });
  });

  if (ranges.length === 0) return [{ text: line }];

  // Handwritten values first, then by start; if equal, prefer longer match.
  ranges.sort((a, b) => a.priority - b.priority || a.start - b.start || (b.end - b.start) - (a.end - a.start));

  // Drop ranges that overlap one already kept.
  const kept = [];
  for (const r of ranges) {
    if (kept.some((k) => r.start < k.end && k.start < r.end)) continue;
    kept.push(r);
  }
  kept.sort((a, b) => a.start - b.start);

  // Stitch into segments.
  const segments = [];
  let pos = 0;
  for (const r of kept) {
    if (r.start > pos) segments.push({ text: line.slice(pos, r.start) });
    segments.push(r.mark ? { mark: r.mark, text: line.slice(r.start, r.end) } : {
      match: true,
      text: line.slice(r.start, r.end),
      idx: r.idx,
      annotation: r.ann,
    });
    pos = r.end;
  }
  if (pos < line.length) segments.push({ text: line.slice(pos) });
  return segments;
}
